import { Icon } from "@iconify/react/dist/iconify.js";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactApexChart from "react-apexcharts";
import { handleError, handleSuccess } from "../CommonJquery/CommonJquery";
import MasterLayout from "../masterLayout/MasterLayout";
import {
  get_social_dashboard,
  server_post_data,
} from "../ServiceConnection/serviceconnection";
import Loader from "./Loader";
import "../components/CSS/gmb-premium.css";
import "../components/CSS/social-dashboard.css";

/* ---------------------------------------------------------------------------
   Config
--------------------------------------------------------------------------- */

const PRESETS = [
  { key: 7, label: "7D" },
  { key: 30, label: "30D" },
  { key: 90, label: "90D" },
];

const KPI_CARDS = [
  { key: "reach", label: "Total Reach", icon: "mdi:eye-outline" },
  { key: "engagement", label: "Engagements", icon: "mdi:gesture-tap" },
  {
    key: "followers",
    label: "Total Audience",
    icon: "mdi:account-group-outline",
  },
  { key: "clicks", label: "Clicks", icon: "mdi:cursor-default-click-outline" },
  { key: "posts", label: "Posts Published", icon: "mdi:post-outline" },
];

const STATUS_LABELS = {
  connected: "Live",
  limited: "Partial",
  error: "Error",
  not_connected: "Not linked",
};

const daysAgoISO = (n) =>
  new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
const todayISO = () => new Date().toISOString().slice(0, 10);

/* ---------------------------------------------------------------------------
   Helpers
--------------------------------------------------------------------------- */

const compact = (n) => {
  if (n === null || n === undefined) return "—";
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
};

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
      })
    : "—";

/* Reads a live CSS custom property so the charts follow dark mode without a
   second hardcoded palette. */
const cssVar = (name, fallback) => {
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return v || fallback;
};

/* ------------------------------------------------------------------------ */

function SocialDashboard() {
  const [loading, setLoading] = useState(false);
  const [dash, setDash] = useState(null);

  const [preset, setPreset] = useState(30);
  const [range, setRange] = useState({
    start_date: daysAgoISO(29),
    end_date: todayISO(),
  });
  const [compare, setCompare] = useState(true);
  const [activeChannels, setActiveChannels] = useState([]); // [] = all
  const [chartMetric, setChartMetric] = useState("reach");
  const [themeTick, setThemeTick] = useState(0);

  const clickRef = useRef(0);

  /* ------------------------- data ------------------------- */
  const load = useCallback(
    async (opts = {}) => {
      if (clickRef.current === 1) return;
      clickRef.current = 1;
      setLoading(true);

      const fd = new FormData();
      fd.append("start_date", opts.start_date || range.start_date);
      fd.append("end_date", opts.end_date || range.end_date);
      fd.append("compare", compare ? "1" : "0");
      if (opts.refresh) fd.append("refresh", "1");

      try {
        const res = await server_post_data(get_social_dashboard, fd);
        const parts = res.message.split("~@~");
        if (parseInt(parts[0]) === 1) {
          handleError(parts[1]);
        } else {
          const parsed = JSON.parse(parts[1]);
          if (parsed.error) {
            handleError(parsed.message || "Could not build the dashboard");
          } else {
            setDash(parsed);
            if (opts.refresh) handleSuccess("Analytics refreshed");
          }
        }
      } catch (e) {
        handleError("network");
      }

      setLoading(false);
      clickRef.current = 0;
    },
    [range, compare],
  );

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Charts read their colours from CSS variables, so a theme switch has to
  // force a re-render for them to pick up the new values.
  useEffect(() => {
    const observer = new MutationObserver(() => setThemeTick((t) => t + 1));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  const applyPreset = (days) => {
    const next = {
      start_date: daysAgoISO(days - 1),
      end_date: todayISO(),
    };
    setPreset(days);
    setRange(next);
    load(next);
  };

  /* ------------------------- derived ------------------------- */
  const visiblePlatforms = useMemo(() => {
    if (!dash) return [];
    if (activeChannels.length === 0) return dash.platforms;
    return dash.platforms.filter((p) => activeChannels.includes(p.key));
  }, [dash, activeChannels]);

  // Filtering by channel has to recompute the headline numbers client-side —
  // refetching the whole dashboard for a chip toggle would be wasteful.
  const summary = useMemo(() => {
    if (!dash) return null;
    if (activeChannels.length === 0) return dash.summary;

    const totals = {
      reach: 0,
      engagement: 0,
      followers: 0,
      clicks: 0,
      posts: 0,
    };
    const measured = {
      ...Object.fromEntries(Object.keys(totals).map((k) => [k, false])),
    };

    visiblePlatforms.forEach((p) => {
      if (!["connected", "limited"].includes(p.status)) return;
      Object.keys(totals).forEach((k) => {
        const v = p.metrics[k];
        if (v !== null && v !== undefined) {
          totals[k] += v;
          measured[k] = true;
        }
      });
    });

    return {
      ...Object.fromEntries(
        Object.keys(totals).map((k) => [k, measured[k] ? totals[k] : null]),
      ),
      engagementRate: totals.reach
        ? Number(((totals.engagement / totals.reach) * 100).toFixed(2))
        : 0,
    };
  }, [dash, activeChannels, visiblePlatforms]);

  const leaderboard = useMemo(() => {
    if (!dash) return [];
    if (activeChannels.length === 0) return dash.leaderboard;
    return dash.leaderboard.filter((r) => activeChannels.includes(r.key));
  }, [dash, activeChannels]);

  const topPosts = useMemo(() => {
    if (!dash) return [];
    if (activeChannels.length === 0) return dash.topPosts;
    return dash.topPosts.filter((p) => activeChannels.includes(p.platform));
  }, [dash, activeChannels]);

  const delta = (key) => {
    if (!dash?.previousSummary || activeChannels.length > 0) return null;
    const prev = dash.previousSummary[key];
    const now = summary?.[key];
    if (!prev || now === null || now === undefined) return null;
    const change = ((now - prev) / prev) * 100;
    return {
      value: Math.abs(Math.round(change)),
      dir: change > 1 ? "up" : change < -1 ? "down" : "flat",
    };
  };

  const toggleChannel = (key) => {
    setActiveChannels((s) =>
      s.includes(key) ? s.filter((k) => k !== key) : [...s, key],
    );
  };

  /* ------------------------- charts ------------------------- */
  const inkColor = cssVar("--text-secondary-light", "#6b7280");
  const lineColor = cssVar("--border-color", "#d1d5db80");

  const trendChart = useMemo(() => {
    const timeline = dash?.timeline || [];

    // With channels filtered, the merged totals no longer match — so the
    // trend switches to per-platform series instead of a single blended line.
    const perPlatform = activeChannels.length > 0;

    const series = perPlatform
      ? visiblePlatforms
          .filter((p) => (p.series || []).length)
          .map((p) => ({
            name: p.label,
            data: p.series.map((pt) => ({
              x: pt.date,
              y: pt[chartMetric] ?? 0,
            })),
          }))
      : [
          {
            name: chartMetric === "reach" ? "Reach" : "Engagements",
            data: timeline.map((pt) => ({
              x: pt.date,
              y: pt[chartMetric] ?? 0,
            })),
          },
        ];

    const colors = perPlatform
      ? visiblePlatforms
          .filter((p) => (p.series || []).length)
          .map((p) => p.color)
      : [cssVar("--primary-600", "#0e71fa")];

    return {
      series,
      options: {
        chart: {
          type: "area",
          height: 320,
          toolbar: { show: false },
          zoom: { enabled: false },
          fontFamily: "inherit",
          background: "transparent",
        },
        colors,
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 2.5 },
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.35,
            opacityTo: 0.02,
            stops: [0, 95],
          },
        },
        grid: {
          borderColor: lineColor,
          strokeDashArray: 4,
          padding: { left: 8, right: 8 },
        },
        xaxis: {
          type: "datetime",
          labels: { style: { colors: inkColor, fontSize: "11px" } },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          labels: {
            style: { colors: inkColor, fontSize: "11px" },
            formatter: (v) => compact(Math.round(v)),
          },
        },
        legend: {
          show: perPlatform,
          position: "top",
          horizontalAlign: "right",
          labels: { colors: inkColor },
          markers: { radius: 12 },
        },
        tooltip: {
          theme: "light",
          x: { format: "dd MMM yyyy" },
          y: { formatter: (v) => Number(v).toLocaleString("en-IN") },
        },
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dash, activeChannels, chartMetric, visiblePlatforms, themeTick]);

  const mixChart = useMemo(() => {
    const rows = leaderboard.filter((r) => r.engagement);
    return {
      series: rows.map((r) => r.engagement),
      options: {
        chart: {
          type: "donut",
          height: 300,
          fontFamily: "inherit",
          background: "transparent",
        },
        labels: rows.map((r) => r.label),
        colors: rows.map((r) => r.color),
        stroke: { width: 0 },
        dataLabels: { enabled: false },
        legend: {
          position: "bottom",
          labels: { colors: inkColor },
          fontSize: "12px",
          markers: { radius: 12 },
          itemMargin: { horizontal: 8, vertical: 3 },
        },
        plotOptions: {
          pie: {
            donut: {
              size: "72%",
              labels: {
                show: true,
                name: { color: inkColor, fontSize: "12px" },
                value: {
                  color: cssVar("--text-primary-light", "#111827"),
                  fontSize: "24px",
                  fontWeight: 800,
                  formatter: (v) => compact(Number(v)),
                },
                total: {
                  show: true,
                  label: "Engagements",
                  color: inkColor,
                  fontSize: "11px",
                  formatter: (w) =>
                    compact(w.globals.seriesTotals.reduce((a, b) => a + b, 0)),
                },
              },
            },
          },
        },
        tooltip: {
          y: { formatter: (v) => Number(v).toLocaleString("en-IN") },
        },
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaderboard, themeTick]);

  const rateChart = useMemo(() => {
    const rows = leaderboard.filter((r) => r.reach);
    return {
      series: [
        {
          name: "Engagement rate",
          data: rows.map((r) => r.engagementRate),
        },
      ],
      options: {
        chart: {
          type: "bar",
          height: 300,
          toolbar: { show: false },
          fontFamily: "inherit",
          background: "transparent",
        },
        colors: rows.map((r) => r.color),
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 5,
            barHeight: "58%",
            distributed: true,
          },
        },
        dataLabels: {
          enabled: true,
          formatter: (v) => `${v}%`,
          style: { fontSize: "11px", fontWeight: 700 },
          offsetX: 26,
        },
        legend: { show: false },
        grid: { borderColor: lineColor, strokeDashArray: 4 },
        xaxis: {
          categories: rows.map((r) => r.label),
          labels: {
            style: { colors: inkColor, fontSize: "11px" },
            formatter: (v) => `${v}%`,
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          labels: { style: { colors: inkColor, fontSize: "11px" } },
        },
        tooltip: { y: { formatter: (v) => `${v}%` } },
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaderboard, themeTick]);

  /* ------------------------- render ------------------------- */
  const maxEngagement = Math.max(
    ...leaderboard.map((r) => r.engagement || 0),
    1,
  );

  return (
    <MasterLayout>
       <div className="gmb-premium sd-dash sd-dash-page">
        {loading && <Loader />}

        {/* ------------------------- Topbar ------------------------- */}
        <div className="gmb-topbar">
          <div className="gmb-topbar-title">
            <h5 className="gmb-display">Social Analytics</h5>
            <span className="gmb-royal-badge">
              <Icon icon="mdi:chart-timeline-variant" /> Master Dashboard
            </span>
            {dash && (
              <span className="gmb-live">
                <span className="gmb-live-dot" />
                {dash.connectedCount} of {dash.totalCount} live
              </span>
            )}
          </div>
          <div className="gmb-topbar-actions">
            <button
              className="btn btn-outline-primary-600"
              onClick={() => load({ refresh: true })}
            >
              <Icon icon="mdi:refresh" /> Refresh
            </button>
          </div>
        </div>

        {/* ------------------------- Controls ------------------------- */}
        <div className="sd-controls">
          <div>
            <label>Quick Range</label>
            <div className="sd-preset-group">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  className={preset === p.key ? "active" : ""}
                  onClick={() => applyPreset(p.key)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label>Start Date</label>
            <input
              type="date"
              className="form-control"
              value={range.start_date}
              onChange={(e) => {
                setPreset(0);
                setRange((s) => ({ ...s, start_date: e.target.value }));
              }}
            />
          </div>
          <div>
            <label>End Date</label>
            <input
              type="date"
              className="form-control"
              value={range.end_date}
              onChange={(e) => {
                setPreset(0);
                setRange((s) => ({ ...s, end_date: e.target.value }));
              }}
            />
          </div>

          <div className="sd-controls-right">
            <div className="d-flex align-items-center gap-2 mb-2">
              <input
                type="checkbox"
                id="sdCompare"
                checked={compare}
                onChange={(e) => setCompare(e.target.checked)}
              />
              <label htmlFor="sdCompare" style={{ margin: 0 }}>
                Compare period
              </label>
            </div>
            <button className="btn btn-primary-600" onClick={() => load()}>
              <Icon icon="mdi:magnify" /> Apply
            </button>
          </div>
        </div>

        {dash && (
          <div className="sd-meta-line">
            <Icon icon="mdi:clock-outline" />
            Built in {dash.fetchSeconds}s · {fmtDate(dash.startDate)} –{" "}
            {fmtDate(dash.endDate)} ({dash.days} days)
            {dash.fromCache && <span className="sd-cache-chip">Cached</span>}
            {dash.previousSummary && (
              <span>
                · vs {fmtDate(dash.previousSummary.startDate)} –{" "}
                {fmtDate(dash.previousSummary.endDate)}
              </span>
            )}
          </div>
        )}

        {/* ------------------------- Channel filter ------------------------- */}
        {dash && (
          <div className="sd-channel-strip">
            <button
              className={`sd-channel-chip ${
                activeChannels.length === 0 ? "active" : ""
              }`}
              style={
                activeChannels.length === 0
                  ? { background: "var(--gmb-royal)" }
                  : undefined
              }
              onClick={() => setActiveChannels([])}
            >
              <Icon icon="mdi:apps" /> All Channels
            </button>

            {dash.platforms.map((p) => {
              const active = activeChannels.includes(p.key);
              const dotClass =
                p.status === "connected"
                  ? "ok"
                  : p.status === "limited"
                    ? "warn"
                    : p.status === "error"
                      ? "err"
                      : "off";
              return (
                <button
                  key={p.key}
                  className={`sd-channel-chip ${active ? "active" : ""} ${
                    p.status === "not_connected" ? "muted" : ""
                  }`}
                  style={active ? { background: p.color } : undefined}
                  onClick={() => toggleChannel(p.key)}
                >
                  <span
                    className="sd-channel-icon"
                    style={{
                      background: active ? "rgba(255,255,255,.22)" : p.color,
                    }}
                  >
                    <Icon icon={p.icon} />
                  </span>
                  {p.label}
                  <span className={`sd-channel-dot ${dotClass}`} />
                </button>
              );
            })}
          </div>
        )}

        {/* ------------------------- KPI row ------------------------- */}
        {!dash ? (
          <div className="row g-3 mb-4">
            {[...Array(5)].map((_, i) => (
              <div className="col-xl col-lg-4 col-md-6" key={i}>
                <div className="sd-skeleton sd-skeleton-kpi" />
              </div>
            ))}
          </div>
        ) : (
          <div className="row g-3 mb-4">
            {KPI_CARDS.map((c) => {
              const value = summary?.[c.key];
              const d = delta(c.key);
              return (
                <div className="col-xl col-lg-4 col-md-6" key={c.key}>
                  <div className="gmb-card sd-kpi">
                    <div className="sd-kpi-head">
                      <span className="sd-kpi-icon">
                        <Icon icon={c.icon} />
                      </span>
                      <span className="sd-kpi-label">{c.label}</span>
                    </div>
                    <div
                      className={`sd-kpi-value ${value === null ? "muted" : ""}`}
                    >
                      {compact(value)}
                    </div>
                    {d ? (
                      <span className={`sd-delta ${d.dir}`}>
                        <Icon
                          icon={
                            d.dir === "up"
                              ? "mdi:trending-up"
                              : d.dir === "down"
                                ? "mdi:trending-down"
                                : "mdi:trending-neutral"
                          }
                        />
                        {d.value}%
                      </span>
                    ) : (
                      <span className="sd-delta flat">
                        <Icon icon="mdi:minus" /> No comparison
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ------------------------- Trend + mix ------------------------- */}
        <div className="row g-3 mb-3">
          <div className="col-xl-8">
            <div className="gmb-card sd-panel">
              <div className="sd-panel-head">
                <div className="sd-panel-title">Performance Over Time</div>
                <div className="sd-toggle-group">
                  {["reach", "engagement"].map((m) => (
                    <button
                      key={m}
                      className={chartMetric === m ? "active" : ""}
                      onClick={() => setChartMetric(m)}
                    >
                      {m === "reach" ? "Reach" : "Engagement"}
                    </button>
                  ))}
                </div>
              </div>
              <div className="sd-panel-sub">
                {activeChannels.length > 0
                  ? "Each selected channel plotted separately."
                  : "All channels combined. Reach counts impressions and views together, which are similar but not identical units."}
              </div>

              {!dash ? (
                <div className="sd-skeleton sd-skeleton-chart" />
              ) : (dash.timeline || []).length === 0 &&
                activeChannels.length === 0 ? (
                <div className="gmb-empty">
                  No daily data available for this period.
                </div>
              ) : (
                <div className="sd-chart-wrap">
                  <ReactApexChart
                    key={`trend-${themeTick}-${activeChannels.join()}-${chartMetric}`}
                    options={trendChart.options}
                    series={trendChart.series}
                    type="area"
                    height={320}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="col-xl-4">
            <div className="gmb-card sd-panel">
              <div className="sd-panel-title">Engagement Mix</div>
              <div className="sd-panel-sub">
                Which channels your interactions come from.
              </div>

              {!dash ? (
                <div className="sd-skeleton sd-skeleton-chart" />
              ) : mixChart.series.length === 0 ? (
                <div className="gmb-empty">No engagement recorded.</div>
              ) : (
                <ReactApexChart
                  key={`mix-${themeTick}-${activeChannels.join()}`}
                  options={mixChart.options}
                  series={mixChart.series}
                  type="donut"
                  height={300}
                />
              )}
            </div>
          </div>
        </div>

        {/* ------------------------- Leaderboard + rate ------------------------- */}
        <div className="row g-3 mb-3">
          <div className="col-xl-7">
            <div className="gmb-card sd-panel">
              <div className="sd-panel-title">Channel Leaderboard</div>
              <div className="sd-panel-sub">
                Ranked by total engagement in this period.
              </div>

              {leaderboard.length === 0 ? (
                <div className="gmb-empty">
                  No connected channels reported data.
                </div>
              ) : (
                leaderboard.map((r, i) => (
                  <div className="sd-lb-row" key={r.key}>
                    <span className="sd-lb-rank">{i + 1}</span>
                    <span
                      className="sd-lb-icon"
                      style={{ background: r.color }}
                    >
                      <Icon icon={r.icon} />
                    </span>
                    <div className="sd-lb-body">
                      <div className="sd-lb-name">{r.label}</div>
                      <div className="sd-lb-bar">
                        <span
                          className="sd-lb-fill"
                          style={{
                            width: `${((r.engagement || 0) / maxEngagement) * 100}%`,
                            background: r.color,
                          }}
                        />
                      </div>
                    </div>
                    <div className="sd-lb-stats">
                      <div>
                        <b>{compact(r.engagement)}</b>
                        <small>Engage</small>
                      </div>
                      <div>
                        <b>{compact(r.reach)}</b>
                        <small>Reach</small>
                      </div>
                      <div>
                        <b>{compact(r.followers)}</b>
                        <small>Audience</small>
                      </div>
                      <div>
                        <b>{r.engagementRate}%</b>
                        <small>Rate</small>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="col-xl-5">
            <div className="gmb-card sd-panel">
              <div className="sd-panel-title">Engagement Rate</div>
              <div className="sd-panel-sub">
                Interactions as a share of reach — a fairer comparison than raw
                totals.
              </div>

              {rateChart.series[0].data.length === 0 ? (
                <div className="gmb-empty">
                  No channel reported both reach and engagement.
                </div>
              ) : (
                <ReactApexChart
                  key={`rate-${themeTick}-${activeChannels.join()}`}
                  options={rateChart.options}
                  series={rateChart.series}
                  type="bar"
                  height={300}
                />
              )}
            </div>
          </div>
        </div>

        {/* ------------------------- Insights + status ------------------------- */}
        <div className="row g-3 mb-3">
          <div className="col-xl-6">
            <div className="gmb-card sd-panel">
              <div className="sd-panel-title">What Changed</div>
              <div className="sd-panel-sub">
                Observations from the data in this period.
              </div>

              {!dash?.insights?.length ? (
                <div className="gmb-empty">
                  Not enough data yet to draw observations.
                </div>
              ) : (
                dash.insights.map((n, i) => (
                  <div className="sd-insight" key={i}>
                    <span className={`sd-insight-icon ${n.type}`}>
                      <Icon
                        icon={
                          n.type === "positive"
                            ? "mdi:trending-up"
                            : n.type === "negative"
                              ? "mdi:trending-down"
                              : "mdi:information-outline"
                        }
                      />
                    </span>
                    <div>
                      <b>{n.title}</b>
                      <p>{n.body}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="col-xl-6">
            <div className="gmb-card sd-panel">
              <div className="sd-panel-title">Channel Status</div>
              <div className="sd-panel-sub">
                Where the numbers came from, and what's missing.
              </div>

              {!dash ? (
                <div className="gmb-empty">Loading...</div>
              ) : (
                dash.platforms.map((p) => (
                  <div className="sd-status-row" key={p.key}>
                    <span
                      className="sd-lb-icon"
                      style={{
                        background: p.color,
                        width: 26,
                        height: 26,
                        fontSize: 13,
                      }}
                    >
                      <Icon icon={p.icon} />
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <b>{p.label}</b>
                      {p.account?.handle && (
                        <span style={{ color: "var(--gmb-ink-3)" }}>
                          {" "}
                          @{p.account.handle}
                        </span>
                      )}
                      {p.message && (
                        <div className="sd-status-msg">{p.message}</div>
                      )}
                    </div>
                    <span className={`sd-status-chip ${p.status}`}>
                      {STATUS_LABELS[p.status]}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* ------------------------- Top content ------------------------- */}
        <div className="gmb-card sd-panel">
          <div className="sd-panel-title">Top Content Across Channels</div>
          <div className="sd-panel-sub">
            Your best-performing posts in this period, ranked by engagement.
          </div>

          {topPosts.length === 0 ? (
            <div className="gmb-empty">
              No posts with engagement data in this period.
            </div>
          ) : (
            topPosts.map((p, i) => (
              <div className="sd-post-row" key={`${p.platform}-${p.id || i}`}>
                {p.image ? (
                  <img className="sd-post-thumb" src={p.image} alt="" />
                ) : (
                  <span className="sd-post-thumb-empty">
                    <Icon icon="mdi:text-box-outline" />
                  </span>
                )}

                <div className="sd-post-body">
                  <div className="sd-post-text">{p.text || "(no text)"}</div>
                  <div className="sd-post-meta">
                    <span
                      className="sd-post-badge"
                      style={{ background: p.platformColor }}
                    >
                      <Icon icon={p.platformIcon} />
                      {p.platformLabel}
                    </span>
                    <span>{fmtDate(p.date)}</span>
                    {p.permalink && (
                      <a
                        className="gmb-competitor-maps-link"
                        href={p.permalink}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View
                      </a>
                    )}
                  </div>
                </div>

                <div className="sd-post-stat">
                  <b>{compact(p.engagement)}</b>
                  <small>Engagements</small>
                </div>
                {p.reach !== null && p.reach !== undefined && (
                  <div className="sd-post-stat">
                    <b>{compact(p.reach)}</b>
                    <small>Reach</small>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </MasterLayout>
  );
}

export default SocialDashboard;
